/**
 * The bottom sheet both item sheets sit in.
 *
 * Owns only the frame: the scrim, the panel, the header and the two ways out
 * that every sheet needs (Escape, and a press that starts and ends on the
 * scrim). What goes inside is entirely the caller's.
 */

import type { ReactNode } from "react";

import { useFocusTrap } from "../lib/a11y/useFocusTrap";
import { Icon } from "./Icon";
import type { IconName } from "./Icon";

import "./Sheet.css";

interface SheetProps {
  title: string;
  icon: IconName;
  onClose: () => void;
  children: ReactNode;
}

export function Sheet({ title, icon, onClose, children }: SheetProps) {
  // Escape used to be handled here directly. It moved into the trap so that
  // closing, trapping and restoring focus are one behaviour rather than three
  // that each dialog could get differently — and so no dialog can implement
  // two of the three and look finished.
  const panelRef = useFocusTrap<HTMLDivElement>({ onEscape: onClose });

  return (
    <div
      className="sheet__scrim"
      // Only a press that starts on the scrim itself closes, so a drag that
      // began inside the panel doesn't dismiss it.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sheet-title"
        ref={panelRef}
      >
        <header className="sheet__header">
          <h2 className="sheet__title" id="sheet-title">
            <Icon name={icon} className="sheet__title-icon" />
            {title}
          </h2>
          <button className="sheet__close" type="button" onClick={onClose} aria-label="Close">
            <Icon name="close" />
          </button>
        </header>

        {children}
      </div>
    </div>
  );
}
